import { useState, useEffect, useMemo, useRef } from 'react'
import { Mountain, ChevronDown, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useWS } from '../contexts/WSContext'
import { useScope } from '../contexts/ScopeContext'

// Linha do dropdown. Fica no MÓDULO: definida dentro do render, o React via um TIPO
// novo a cada frame de telemetria (o componente lê o contexto do WS), remontava as
// linhas e o clique caía no nó substituído. Mesma armadilha do DeviceCommands.
function Option({ value, label, count, selected, onPick }) {
  return (
    <button onClick={() => onPick(value)}
      className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-left hover:bg-slate-50 dark:hover:bg-slate-800">
      <span className="w-4 shrink-0 flex items-center justify-center">
        {selected && <Check size={14} className="text-brand-600" />}
      </span>
      <span className={`flex-1 truncate ${selected ? 'font-medium text-slate-800 dark:text-slate-100' : 'text-slate-700 dark:text-slate-200'}`}>{label}</span>
      <span className="text-xs text-slate-400 dark:text-slate-500 shrink-0 tabular-nums">{count}</span>
    </button>
  )
}

// Seletor global de escopo de mina (Topbar): single-select com "Todas as minas",
// uma opção por mina e "Sem mina" sempre por último.
export default function MineScopeSelect() {
  const { t } = useTranslation()
  const { mines, devices } = useWS()
  const { mineScope, setMineScope, scopeLabel } = useScope()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // Uma varredura por render, não uma por opção.
  const counts = useMemo(() => {
    const byMine = new Map()
    let none = 0
    for (const d of devices) {
      if (d.mineId == null) { none++; continue }
      byMine.set(d.mineId, (byMine.get(d.mineId) || 0) + 1)
    }
    return { total: devices.length, none, byMine }
  }, [devices])
  const countFor = (value) =>
    value === 'all' ? counts.total : value === 'none' ? counts.none : (counts.byMine.get(value) || 0)

  const pick = (value) => { setMineScope(value); setOpen(false) }

  const mineOptions = useMemo(() => [...mines]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(m => ({ value: Number(m.id), label: m.name })), [mines])

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 min-w-[200px] max-w-[260px] pl-3 pr-2 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand/30">
        <Mountain size={15} className="text-slate-400 dark:text-slate-500 shrink-0" />
        <span className="truncate flex-1 text-left">{scopeLabel}</span>
        <ChevronDown size={15} className={`text-slate-400 dark:text-slate-500 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-72 max-h-80 overflow-y-auto scrollbar-thin bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg py-1">
          <Option value="all" label={t('common:scope.all_mines')} count={countFor('all')} selected={mineScope === 'all'} onPick={pick} />
          {mineOptions.length === 0 ? (
            <p className="px-3 py-3 text-sm text-slate-400 dark:text-slate-500">{t('ops:scope.no_mines')}</p>
          ) : (
            mineOptions.map(o => (
              <Option key={o.value} value={o.value} label={o.label} count={countFor(o.value)} selected={mineScope === o.value} onPick={pick} />
            ))
          )}
          <div className="my-1 border-t border-slate-100 dark:border-slate-800" />
          <Option value="none" label={t('common:scope.no_mine')} count={countFor('none')} selected={mineScope === 'none'} onPick={pick} />
        </div>
      )}
    </div>
  )
}

// Chip do escopo ativo, exibido nas telas de produção quando o escopo não é global.
export function ScopeChip() {
  const { mineScope, scopeLabel } = useScope()
  if (mineScope === 'all') return null
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-brand/30 bg-brand/10 text-brand-700 dark:text-brand-300 text-xs font-medium">
      <Mountain size={11} className="shrink-0" /> {scopeLabel}
    </span>
  )
}
